import { GetRoiTimelineViewIO } from '../types';
import { useAnalyticsGateway1Queries } from './use-analytics-gateway1-queries';
import { UseAnalyticsGateway1QueryEndpointArgs, useAnalyticsGateway1Query } from './use-analytics-gateway1-query';

/**
 * A hook that returns a query for the `GetRoiTimelineView` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetRoiTimelineViewQuery = <E = unknown, D = GetRoiTimelineViewIO['output']>(
  args: UseAnalyticsGateway1QueryEndpointArgs<'GetRoiTimelineView', E, D>
) =>
  useAnalyticsGateway1Query({
    endpointName: 'GetRoiTimelineView',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetRoiTimelineView` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetRoiTimelineViewQueries = <E = unknown, D = GetRoiTimelineViewIO['output']>(
  argsArr: UseAnalyticsGateway1QueryEndpointArgs<'GetRoiTimelineView', E, D>[]
) =>
  useAnalyticsGateway1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetRoiTimelineView',
      ...args,
    }))
  );
